import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Avatar, Checkbox, TextField, useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CheckboxIcon } from '@icons/CheckboxIcon';
import { insertAt } from '@utils/CommonUtil';
import { InputDataPopup } from '../action/InputDataPopup';

type ShowData = {
  isShow: boolean;
  index?: number;
  cursorPos?: number; // 텍스트필드 내 커서 위치
};

export const ConditionConfig = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { workflowDetailStore } = useStore();
  const isEditTable = workflowDetailStore.checkEditable();
  const [showData, setShowData] = useState<ShowData>({ isShow: false });
  const focusedInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // MobX observer 패턴: render 내부에서 직접 접근하여 반응성 유지
  const getActionModel = () => {
    const conditionNode = workflowDetailStore.findNode(workflowDetailStore.curNodeId ?? '');
    return conditionNode?.data?.actionModel;
  };

  const handleChangeCheckbox = (checked: boolean, argIndex: number) => {
    const actionModel = getActionModel();
    if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments?.[argIndex]) return;

    actionModel.actionArguments[argIndex].expressionType = checked ? 'CUSTOM' : 'SIMPLE';
  };

  const handleChangeTextfieldParam = (event: React.ChangeEvent<HTMLInputElement>, argIndex: number) => {
    const actionModel = getActionModel();
    if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments?.[argIndex]) return;

    actionModel.actionArguments[argIndex].expression = event.target.value;
  };

  const handleClickInputDataPopup = (
    type: 'action' | 'trigger',
    field: string,
    displayName: string,
    argIndex: number,
  ) => {
    const actionModel = getActionModel();
    if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments) return;

    if (type === 'trigger') {
      actionModel.actionArguments[argIndex].expression = insertAt(
        actionModel.actionArguments[argIndex].expression ?? '',
        showData.cursorPos,
        `#{${field}}`,
      );
    }

    // 선택 시 포커스 이동
    if (focusedInputRef.current) {
      focusedInputRef.current.focus();
    }
  };

  const handleCloseInputDataPopup = () => {
    setShowData({ isShow: false, index: undefined });
  };

  const actionModel = getActionModel();

  return (
    <Container isEditTable={isEditTable}>
      <Body>
        {!isEditTable && (
          <>
            <ToolbarName>{t('Workflow.condition.condition_expression')}</ToolbarName>
            <Avatar variant="square" sx={{ width: '56px', height: '56px' }} style={{ borderRadius: '8px' }}>
              IF
            </Avatar>
            <ConditionDescription>{t('Workflow_Create_IfActionDesc')}</ConditionDescription>
          </>
        )}

        <InputParams>
          <Hr />
          {actionModel?.actionArguments?.map((arg, argIndex) => {
            if (arg.argumentName !== 'expr') return null;

            return (
              <InputParam key={arg.argumentName}>
                <InputParamHeader>
                  <InputParamLabel>
                    {t('Workflow.action.condition_expr')}
                    <InputParamLabelStar>*</InputParamLabelStar>
                  </InputParamLabel>
                  <InputParamCustom>
                    <Checkbox
                      icon={<CheckboxIcon width={16} height={16} color={theme.colors['border-gray-light']} />}
                      checkedIcon={
                        <CheckboxIcon
                          width={16}
                          height={16}
                          color={theme.colors['ic-white']}
                          bgColor={theme.colors['bg-primary']}
                        />
                      }
                      checked={arg.expressionType === 'CUSTOM'}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                        if (checked) {
                          setShowData(prev => {
                            return { ...prev, isShow: false };
                          });
                        }
                        handleChangeCheckbox(checked, argIndex);
                      }}
                    />
                    <span>{t('Workflow_Create_37')}</span>
                  </InputParamCustom>
                </InputParamHeader>
                <TextField
                  value={arg.expression ?? ''}
                  fullWidth
                  multiline
                  placeholder={t('Workflow.condition.expression_placeholder')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeTextfieldParam(event, argIndex)}
                  onFocus={e => {
                    focusedInputRef.current = e.target;
                  }}
                  // NOTE. 문자열의 커서 위치가 변경된 경우 처리
                  onSelect={() => {
                    // NOTE. 수정 모드인 경우에만 입력데이터 표시
                    if (!workflowDetailStore.checkEditable()) return;
                    setShowData({
                      isShow: true,
                      index: argIndex,
                      cursorPos: focusedInputRef.current?.selectionStart ?? undefined,
                    });
                  }}
                  autoComplete="off"
                  InputProps={{
                    // NOTE. 수정 모드가 아닌 경우, readOnly 모드
                    readOnly: !workflowDetailStore.checkEditable(),
                  }}
                />
              </InputParam>
            );
          })}
        </InputParams>

        {showData.isShow && (
          <InputDataPopup
            triggerId={workflowDetailStore.triggerNode?.data.baseTriggerModel?.id}
            onClick={handleClickInputDataPopup}
            onClose={handleCloseInputDataPopup}
            index={showData.index as number}
          />
        )}
      </Body>
    </Container>
  );
});

const Container = styled.div<{ isEditTable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  background: inherit;
  padding: 36px 32px;
  ${({ isEditTable }) => isEditTable && 'padding: 0px;'}
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: inherit;
  border: 0px;
  padding: 0;
  position: relative;
`;

const ConditionDescription = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  border-radius: 4px;
  background-color: ${props => props.theme.colors['bg-primary-lighter']};
  padding: 18px 24px;
  ${fonts.Body4};
  color: ${props => props.theme.colors['text-primary']};
  margin-bottom: 24px;
  margin-top: 16px;
`;

const InputParams = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputParam = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 6px;
`;

const InputParamHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const InputParamLabel = styled.label`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  ${fonts.Headline8};
  gap: 4px;
`;

const InputParamLabelStar = styled.span`
  color: ${props => props.theme.colors['text-red-light']};
`;

const InputParamCustom = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  ${fonts.Body2};
`;

const Hr = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  margin-bottom: 16px;
`;

const ToolbarName = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-bottom: 18px;
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;
